
if (!customElements.get('product-form')) {
  const UPCART_CART_EVENTS = ['aftersell-upcart:public-events:cart-updated', 'aftersell-upcart:public-events:cart-loaded'];

  const themeKnownCart = { addonInCart: null };

  const isUpcartActive = () => typeof window.upcartRefreshCart === 'function';

  const upcartCartHasAddon = (items) => {
    const cfg = window.themeBundleCartRemove;
    if (!cfg || !cfg.addonProductId) return null;
    return items.some(
      (item) =>
        Number(item.productId) === Number(cfg.addonProductId) &&
        Number(item.sellingPlanAllocation?.sellingPlan?.id) === Number(cfg.addonSellingPlanId)
    );
  };

  UPCART_CART_EVENTS.forEach((name) => {
    window.addEventListener(name, (event) => {
      const items = event.detail && event.detail.cart && event.detail.cart.items;
      if (!Array.isArray(items)) return;
      const hasAddon = upcartCartHasAddon(items);
      if (hasAddon !== null) themeKnownCart.addonInCart = hasAddon;
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') themeKnownCart.addonInCart = null;
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) themeKnownCart.addonInCart = null;
  });

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (themeKnownCart.addonInCart === null && this.dataset.bundleAddonInCart) {
          themeKnownCart.addonInCart = this.dataset.bundleAddonInCart === 'true';
        }
      }

      lineItemPropertiesFromFormData(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (match && String(value).length) properties[match[1]] = value;
        }
        return Object.keys(properties).length ? properties : null;
      }

      findBundleVariantIdFromProductJson(productJson, sellingPlanId) {
        const plan = Number(sellingPlanId);
        const variants = productJson.variants || [];
        for (const v of variants) {
          const allocs = v.selling_plan_allocations || [];
          for (const a of allocs) {
            const sid = a.selling_plan_id ?? a.selling_plan?.id;
            if (Number(sid) === plan) return v.id;
          }
        }
        return null;
      }

      endSubmitLoading() {
        this.submitButton.classList.remove('loading');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
        if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
        if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
      }

      finishSubmit() {
        this.endSubmitLoading();
      }

      cartLineMatchesBundleAddon(line, variantId, sellingPlanId) {
        if (Number(line.variant_id) !== Number(variantId)) return false;
        const spid =
          line.selling_plan_allocation?.selling_plan?.id ??
          line.selling_plan_allocation?.selling_plan_id;
        return Number(spid) === Number(sellingPlanId);
      }

      verifyBundleAddon(response, addonVid, addonPlan, addonSent) {
        const root = window.Shopify?.routes?.root ?? '/';
        const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

        if (addonSent) {
          const line = (response.items || []).find((item) => this.cartLineMatchesBundleAddon(item, addonVid, addonPlan));
          if (!line || Number(line.quantity) <= 1 || !line.key) return;
          fetch(root + 'cart/change.js', { method: 'POST', headers, body: JSON.stringify({ id: line.key, quantity: 1 }) })
            .then(() => window.upcartRefreshCart())
            .catch(() => {});
          return;
        }

        const stopListening = () => UPCART_CART_EVENTS.forEach((name) => window.removeEventListener(name, onCart));
        const onCart = (event) => {
          stopListening();
          const items = (event.detail && event.detail.cart && event.detail.cart.items) || [];
          const triggerIds = ((window.themeBundleCartRemove || {}).triggerProductIds || []).map(Number);
          const hasTrigger = items.some((item) => triggerIds.includes(Number(item.productId)));
          if (!hasTrigger || upcartCartHasAddon(items) !== false) return;
          fetch(root + 'cart/add.js', {
            method: 'POST',
            headers,
            body: JSON.stringify({ items: [{ id: addonVid, quantity: 1, selling_plan: addonPlan }] }),
          })
            .then(() => window.upcartRefreshCart())
            .catch(() => {});
        };
        UPCART_CART_EVENTS.forEach((name) => window.addEventListener(name, onCart));
        setTimeout(stopListening, 5000);
      }

      resolveSellingPlanFromForm(formData) {
        const fromForm = formData.get('selling_plan');
        if (fromForm) return fromForm;

        const hiddenInput = this.form.querySelector('.js-selling-plan-input');
        if (hiddenInput?.value) return hiddenInput.value;

        const buyBoxInput = this.form.querySelector('.js-buy-box-selling-plan:checked');
        if (buyBoxInput?.value) return buyBoxInput.value;

        if (this.dataset.bundleActive === 'true' && this.dataset.bundleSellingPlan) {
          const variantId = Number(formData.get('id'));
          const addonProductId = Number(this.dataset.bundleAddonProductId);
          const addonVariantId = Number(this.dataset.bundleAddVariantId);
          if (addonProductId && variantId === addonVariantId) {
            return this.dataset.bundleSellingPlan;
          }
        }

        return null;
      }

      runCartAddRequest(formData, bundleActive, bundleSellingPlan, effectiveBundleVariantId) {
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const sellingPlan = this.resolveSellingPlanFromForm(formData);
        if (sellingPlan && !formData.get('selling_plan')) {
          formData.set('selling_plan', sellingPlan);
        }

        const useBundleAdd = Boolean(bundleActive && effectiveBundleVariantId && bundleSellingPlan);
        const renderThemeCart = Boolean(this.cart) && !isUpcartActive();
        const addonVid = Number(effectiveBundleVariantId);
        const addonPlan = Number(bundleSellingPlan);
        let addonSent = false;

        let addPromise;
        if (useBundleAdd) {
          if (this.cart) this.cart.setActiveElement(document.activeElement);
          const mainItem = {
            id: Number(formData.get('id')),
            quantity,
          };
          const mainSellingPlan = sellingPlan || formData.get('selling_plan');
          if (mainSellingPlan) mainItem.selling_plan = Number(mainSellingPlan);
          const props = this.lineItemPropertiesFromFormData(formData);
          if (props) mainItem.properties = props;

          const root = window.Shopify?.routes?.root ?? '/';

          const addonInCartPromise =
            themeKnownCart.addonInCart !== null
              ? Promise.resolve(themeKnownCart.addonInCart)
              : fetch(root + 'cart.js')
                  .then((r) => r.json())
                  .then((cart) =>
                    (cart.items || []).some((line) => this.cartLineMatchesBundleAddon(line, addonVid, addonPlan))
                  );

          addPromise = addonInCartPromise.then((addonAlreadyInCart) => {
            addonSent = !addonAlreadyInCart;
            const items = [];
            items.push(mainItem);
            if (!addonAlreadyInCart) {
              items.push({
                id: addonVid,
                quantity: 1,
                selling_plan: addonPlan,
              });
            }

            const payload = { items };
            if (renderThemeCart) {
              payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
              payload.sections_url = window.location.pathname;
            }
            return fetch(`${routes.cart_add_url}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                Accept: 'application/javascript',
              },
              body: JSON.stringify(payload),
            });
          });
        } else {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          if (this.cart) {
            if (renderThemeCart) {
              formData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              formData.append('sections_url', window.location.pathname);
            }
            this.cart.setActiveElement(document.activeElement);
          }
          addPromise = fetch(`${routes.cart_add_url}`, { ...config, body: formData });
        }

        return addPromise
          .then((httpRes) => httpRes.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.error = false;
            if (useBundleAdd) {
              themeKnownCart.addonInCart = true;
              this.verifyBundleAddon(response, addonVid, addonPlan, addonSent);
            }
            return response;
          })
          .then((response) => {
            if (response == null || this.error) return;

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    try {
                      if (response.sections) this.cart.renderContents(response);
                    } catch (_e) {}
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              try {
                if (response.sections) this.cart.renderContents(response);
              } catch (_e) {}
            }
          })
          .catch(() => {
            this.error = true;
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.finishSubmit();
          });
      }

      startBundleAddFlow() {
        const formData = new FormData(this.form);
        const bundleActive = this.dataset.bundleActive === 'true';
        const bundleVariantId = this.dataset.bundleAddVariantId;
        const bundleSellingPlan = this.dataset.bundleSellingPlan;
        const bundleAddonHandle = (this.dataset.bundleAddonHandle || '').trim();

        if (bundleActive && bundleSellingPlan && !bundleVariantId && bundleAddonHandle) {
          const root = window.Shopify?.routes?.root ?? '/';
          const url = `${root}products/${encodeURIComponent(bundleAddonHandle)}.js`;
          fetch(url)
            .then((r) => {
              if (!r.ok) throw new Error(`product.js HTTP ${r.status}`);
              return r.json();
            })
            .then((productJson) => {
              const vid = this.findBundleVariantIdFromProductJson(productJson, bundleSellingPlan);
              if (!vid) {
                throw new Error(
                  'Nenhuma variante com selling_plan_id=' +
                    bundleSellingPlan +
                    ' em product.js (confere plano no admin e o handle do produto extra).'
                );
              }
              return this.runCartAddRequest(formData, bundleActive, bundleSellingPlan, String(vid));
            })
            .catch(() => {
              this.error = false;
              this.finishSubmit();
            });
          return;
        }

        this.runCartAddRequest(formData, bundleActive, bundleSellingPlan, bundleVariantId || null);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        this.startBundleAddFlow();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text, variant) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');

          if (window.variantStrings.atcCustomText) {
            this.submitButtonText.innerHTML = '<span class="atc-custom-text">' + window.variantStrings.atcCustomText
              + ' - '
              + '<span class="compare-price">' + Shopify.formatMoney(variant.compare_at_price) + '</span>'
              + '&nbsp;'
              + Shopify.formatMoney(variant.price)
              + '</span>'
              + '<br>'
              + '<span class="atc-custom-bottom-text">'
              + window.variantStrings.atcCustomBottomText
              + '</span>';
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }

        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
